/**
 * 약관 페이지 (`/terms/:type`).
 *
 * 영구지침: docs/develop/seo/ssr-spec.md §15.2, §15.4
 * - 페이지별 title·canonical (정적 사이트맵 URL)
 * - 설정에 없는 약관 type 은 NotFoundPage(HTTP 404, noindex) — soft-404 방지
 * - 본문은 public/*.md 를 클라이언트에서 fetch (SSR 프리페치 없음)
 */
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import Header from '@/components/layout/Header'
import PageContainer from '@/components/layout/PageContainer'
import Spinner from '@/components/ui/Spinner'
import SEO from '@/components/common/SEO'
import NotFoundPage from '@/pages/NotFoundPage'

const TERMS_CONFIG: Record<string, { title: string; file: string }> = {
  'terms-of-service': { title: '서비스 이용약관', file: '/terms-of-service.md' },
  'privacy-policy': { title: '개인정보 처리방침', file: '/privacy-policy.md' },
  'marketing-consent': { title: '마케팅 정보 수신 동의', file: '/marketing-consent.md' },
  'marketing-personal-info': {
    title: '마케팅 활용 개인정보 수집·이용 동의',
    file: '/marketing-personal-info.md',
  },
  // 문서는 작성돼 있었으나 라우팅되지 않아 열람이 불가능했다. 동의 모달에서 링크한다.
  'sensitive-info-consent': { title: '민감정보 수집 및 이용 동의', file: '/sensitive-info-consent.md' },
  'third-party-consent': { title: '개인정보 제3자 제공 동의', file: '/third-party-consent.md' },
}

export default function TermsPage() {
  const { type } = useParams<{ type: string }>()
  const navigate = useNavigate()
  const [content, setContent] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  const config = type ? TERMS_CONFIG[type] : null

  useEffect(() => {
    if (!config) return
    setIsLoading(true)
    fetch(config.file)
      .then(res => res.text())
      .then(text => {
        setContent(text)
        setIsLoading(false)
      })
      .catch(() => {
        setContent('문서를 불러올 수 없습니다.')
        setIsLoading(false)
      })
  }, [config])

  // 모든 훅 호출 뒤에 조기 return (rules-of-hooks)
  if (!config) {
    return (
      <NotFoundPage
        title="존재하지 않는 약관입니다"
        description="주소가 잘못 입력되었거나 더 이상 제공하지 않는 약관입니다."
      />
    )
  }

  return (
    <>
      <SEO
        title={config.title}
        description={`케어밸런스(CareBalance) ${config.title} 전문입니다.`}
        url={`/terms/${type}`}
      />
      <Header title={config.title} showBack onBack={() => navigate(-1)} />
      <PageContainer>
        {isLoading ? (
          <div className="flex justify-center py-20"><Spinner /></div>
        ) : (
          <article className="terms-content">
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h1 className="text-xl font-bold text-cb-gray-900 mt-8 mb-4 pb-2 border-b border-cb-gray-200">{children}</h1>,
                h2: ({ children }) => <h2 className="text-lg font-bold text-cb-gray-900 mt-6 mb-3">{children}</h2>,
                h3: ({ children }) => <h3 className="text-body font-bold text-cb-gray-800 mt-4 mb-2">{children}</h3>,
                h4: ({ children }) => <h4 className="text-body-sm font-bold text-cb-gray-800 mt-3 mb-1">{children}</h4>,
                p: ({ children }) => <p className="text-body-sm text-cb-gray-700 mb-3 leading-relaxed">{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-5 mb-3 space-y-1">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-5 mb-3 space-y-1">{children}</ol>,
                li: ({ children }) => <li className="text-body-sm text-cb-gray-700 leading-relaxed">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold text-cb-gray-900">{children}</strong>,
                table: ({ children }) => <div className="overflow-x-auto mb-4"><table className="w-full text-body-sm border-collapse border border-cb-gray-200">{children}</table></div>,
                thead: ({ children }) => <thead className="bg-cb-gray-50">{children}</thead>,
                th: ({ children }) => <th className="border border-cb-gray-200 px-3 py-2 text-left font-semibold text-cb-gray-700">{children}</th>,
                td: ({ children }) => <td className="border border-cb-gray-200 px-3 py-2 text-cb-gray-600">{children}</td>,
                hr: () => <hr className="my-6 border-cb-gray-200" />,
                blockquote: ({ children }) => <blockquote className="border-l-4 border-cb-primary pl-4 my-4 bg-cb-gray-50 py-2 rounded-r-lg">{children}</blockquote>,
              }}
            >
              {content}
            </Markdown>
          </article>
        )}
      </PageContainer>
    </>
  )
}
